import React, { useState, useEffect } from "react";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { useNavigate, useLocation } from "react-router-dom";
import {
  MdReportProblem,
  MdArrowBack,
  MdAdd,
  MdDelete,
  MdLocalShipping,
  MdRefresh,
  MdVisibility,
  MdClose,
  MdImage,
  MdEdit,
  MdFileDownload,
} from "react-icons/md";
import toast from "react-hot-toast";
import { tokenManager } from "utils/tokenManager";
import { jwtDecode } from "jwt-decode";
import Loading from "components/loading";

const API_BASE = "https://usibras-web-backend.onrender.com/api";

const SEVERITY_COLORS = {
  Low: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  Medium: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  High: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  Critical: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const STATUS_COLORS = {
  Open: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  "In Progress": "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  Resolved: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  Closed: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400",
};

const formatDate = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString("en-GB") : "—";

const severityOptions = ["Low", "Medium", "High", "Critical"];
const incidentTypeOptions = [
  "Damage",
  "Shortage",
  "Delay",
  "Quality Issue",
  "Theft",
  "Accident",
  "Documentation Error",
  "Other",
];
const statusOptions = ["Open", "In Progress", "Resolved", "Closed"];

const initialFormState = {
  incidentDate: "",
  incidentType: "",
  severity: "",
  status: "Open",
  description: "",
  actionTaken: "",
  reportedBy: "",
};

/* ── View Details Modal ────────────────────────────── */
function ViewDetailsModal({ report, onClose }) {
  if (!report) return null;
  const r = report;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-navy-600 dark:bg-navy-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-navy-600">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">
            Incidence Details
          </h3>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700 dark:hover:text-white"
            aria-label="Close"
          >
            <MdClose className="h-6 w-6" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Incident Date</p>
              <p className="font-medium text-navy-700 dark:text-white">{formatDate(r.incident_date)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Incident Type</p>
              <p className="font-medium text-navy-700 dark:text-white">{r.incident_type}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Severity</p>
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-sm font-medium ${SEVERITY_COLORS[r.severity] || "bg-gray-100 text-gray-800"}`}>
                {r.severity}
              </span>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Status</p>
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-sm font-medium ${STATUS_COLORS[r.status] || "bg-gray-100 text-gray-800"}`}>
                {r.status}
              </span>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Reported By</p>
              <p className="font-medium text-navy-700 dark:text-white">{r.reported_by || "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Date Reported</p>
              <p className="font-medium text-navy-700 dark:text-white">{formatDate(r.created_at)}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Description</p>
              <p className="mt-1 whitespace-pre-wrap font-medium text-navy-700 dark:text-white">{r.description || "—"}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Action Taken</p>
              <p className="mt-1 whitespace-pre-wrap font-medium text-navy-700 dark:text-white">{r.action_taken || "—"}</p>
            </div>
            {r.image_urls && r.image_urls.length > 0 && (
              <div className="sm:col-span-2 space-y-3">
                <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Attachments</p>
                <div className="flex flex-wrap gap-3">
                  {r.image_urls.map((url, idx) => (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative overflow-hidden rounded-lg border border-gray-200 dark:border-navy-600"
                    >
                      <img
                        src={url}
                        alt={`Attachment ${idx + 1}`}
                        className="h-24 w-24 object-cover transition-transform group-hover:scale-105"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Delete Confirm Modal ──────────────────────────── */
function DeleteConfirmModal({ report, onClose, onConfirm, isDeleting }) {
  if (!report) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-navy-600 dark:bg-navy-800">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <MdDelete className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">Delete incidence report?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone.</p>
          </div>
        </div>
        <div className="mb-6 rounded-lg bg-gray-50 p-4 dark:bg-navy-700/50">
          <p className="text-sm font-medium text-navy-700 dark:text-white">{report.incident_type} — {report.severity}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formatDate(report.incident_date)} • Reported by {report.reported_by}
          </p>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(report.id)}
            disabled={isDeleting}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Edit Modal ────────────────────────────────────── */
function EditModal({ report, onClose, onSave }) {
  const [editData, setEditData] = useState({
    incidentDate: report.incident_date ? report.incident_date.slice(0, 10) : "",
    incidentType: report.incident_type || "",
    severity: report.severity || "",
    status: report.status || "Open",
    description: report.description || "",
    actionTaken: report.action_taken || "",
    reportedBy: report.reported_by || "",
  });
  const [newImages, setNewImages] = useState([]);
  const [existingImages, setExistingImages] = useState(report.image_urls || []);
  const [replaceImages, setReplaceImages] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleEditChange = (e) => {
    const { id, name, value } = e.target;
    const field = id || name;
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewImageAdd = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(
      (f) => f.type.startsWith("image/") || f.type === "application/pdf"
    );
    if (validFiles.length !== files.length) {
      toast.error("Some files were skipped — only images and PDFs are allowed");
    }
    setNewImages((prev) => [...prev, ...validFiles]);
    if (validFiles.length > 0) setReplaceImages(true);
    e.target.value = "";
  };

  const handleNewImageRemove = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
    setReplaceImages(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        toast.error("Authentication required");
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append("incident_date", editData.incidentDate);
      formDataToSend.append("incident_type", editData.incidentType);
      formDataToSend.append("severity", editData.severity);
      formDataToSend.append("status", editData.status);
      formDataToSend.append("description", editData.description.trim());
      formDataToSend.append("action_taken", editData.actionTaken.trim());
      formDataToSend.append("reported_by", editData.reportedBy.trim());

      if (replaceImages) {
        formDataToSend.append("replace_images", "true");
        newImages.forEach((file) => {
          formDataToSend.append("images", file);
        });
      }

      const resp = await fetch(`${API_BASE}/incidence-reports/${report.id}`, {
        method: "PUT",
        headers: { Authorization: authHeader },
        body: formDataToSend,
      });

      const result = await resp.json().catch(() => ({}));

      if (resp.ok) {
        toast.success(result.message || "Incidence report updated successfully");
        onSave();
      } else {
        toast.error(result.message || result.error || "Failed to update incidence report");
      }
    } catch (err) {
      toast.error(err.message || "Failed to update incidence report");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-navy-600 dark:bg-navy-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-navy-600">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500 text-white">
              <MdEdit className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-navy-700 dark:text-white">
                Edit Incidence Report
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                * Indicates required field
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700 dark:hover:text-white"
            aria-label="Close"
          >
            <MdClose className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleEditSubmit} className="max-h-[75vh] overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <InputField
              label="Incident Date *"
              id="incidentDate"
              type="date"
              value={editData.incidentDate}
              onChange={handleEditChange}
              required
            />

            <div className="flex flex-col">
              <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                Incident Type *
              </label>
              <select
                id="incidentType"
                value={editData.incidentType}
                onChange={handleEditChange}
                required
                className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm outline-none focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select incident type</option>
                {incidentTypeOptions.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                Severity *
              </label>
              <select
                id="severity"
                value={editData.severity}
                onChange={handleEditChange}
                required
                className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm outline-none focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select severity</option>
                {severityOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                Status
              </label>
              <select
                id="status"
                value={editData.status}
                onChange={handleEditChange}
                className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm outline-none focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <InputField
              label="Reported By *"
              id="reportedBy"
              placeholder="Enter reporter's name"
              value={editData.reportedBy}
              onChange={handleEditChange}
              required
            />

            <div className="flex flex-col md:col-span-2">
              <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                Description *
              </label>
              <textarea
                id="description"
                value={editData.description}
                onChange={handleEditChange}
                required
                rows={4}
                placeholder="Describe the incidence in detail..."
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm outline-none focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
              />
            </div>

            <div className="flex flex-col md:col-span-2">
              <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                Action Taken
              </label>
              <textarea
                id="actionTaken"
                value={editData.actionTaken}
                onChange={handleEditChange}
                rows={3}
                placeholder="Describe any corrective action taken..."
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm outline-none focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
              />
            </div>

            {/* Existing Images */}
            {existingImages.length > 0 && (
              <div className="flex flex-col md:col-span-2">
                <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                  Current Images
                </label>
                <div className="flex flex-wrap gap-3">
                  {existingImages.map((url, index) => (
                    <div
                      key={index}
                      className="group relative overflow-hidden rounded-lg border border-gray-200 dark:border-navy-600"
                    >
                      <img
                        src={url}
                        alt={`Existing ${index + 1}`}
                        className="h-20 w-20 object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveExistingImage(index)}
                        className="absolute -right-1 -top-1 rounded-full bg-red-500 p-0.5 text-white shadow-md hover:bg-red-600"
                        title="Remove"
                      >
                        <MdClose className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload New Images */}
            <div className="flex flex-col md:col-span-2">
              <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                {existingImages.length > 0 ? "Replace / Add New Images" : "Upload Images"}
              </label>
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-6 transition-colors hover:border-brand-500 hover:bg-brand-50/50 dark:border-navy-600 dark:bg-navy-800/50 dark:hover:border-brand-500 dark:hover:bg-navy-800">
                <MdImage className="h-8 w-8 text-gray-400 dark:text-navy-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Click to add images (multiple allowed)
                </span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={handleNewImageAdd}
                  className="hidden"
                />
              </label>

              {newImages.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-3">
                  {newImages.map((file, index) => (
                    <div
                      key={index}
                      className="group relative flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-2 dark:border-navy-600 dark:bg-navy-800"
                    >
                      {file.type.startsWith("image/") ? (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="h-16 w-16 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gray-100 dark:bg-navy-700">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">PDF</span>
                        </div>
                      )}
                      <div className="flex flex-col">
                        <span className="max-w-[120px] truncate text-xs font-medium text-navy-700 dark:text-white">
                          {file.name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {(file.size / 1024).toFixed(0)} KB
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleNewImageRemove(index)}
                        className="ml-1 rounded-full p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/20 dark:hover:text-red-400"
                        title="Remove"
                      >
                        <MdClose className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                Adding new images will replace all existing images on this report.
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="rounded-lg border border-gray-200 bg-white px-6 py-2.5 text-sm font-medium text-navy-700 hover:bg-gray-50 disabled:opacity-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="rounded-lg bg-brand-500 px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {isSaving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Main Component ────────────────────────────────── */
const IncidenceReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const record = location.state?.record;

  const [userRole, setUserRole] = useState("");
  const [formData, setFormData] = useState(initialFormState);
  const [images, setImages] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [viewReport, setViewReport] = useState(null);
  const [deleteReport, setDeleteReport] = useState(null);
  const [editReport, setEditReport] = useState(null);

  useEffect(() => {
    const loginData = localStorage.getItem("usibras_login");
    const userData = localStorage.getItem("usibras_user_data");
    if (loginData || userData) {
      try {
        let decoded;
        if (loginData) {
          try {
            const parsed = JSON.parse(loginData);
            decoded = parsed.userData ? jwtDecode(parsed.userData) : jwtDecode(loginData);
          } catch {
            decoded = jwtDecode(loginData);
          }
        } else {
          decoded = jwtDecode(userData);
        }
        if (decoded?.role) setUserRole(decoded.role);
      } catch (err) {
        console.error("Error decoding role:", err);
      }
    }
  }, []);

  const fetchReports = async () => {
    if (!record?.id) return;
    setLoading(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        toast.error("Authentication required");
        return;
      }
      const resp = await fetch(
        `${API_BASE}/incidence-reports`,
        { headers: { Authorization: authHeader } }
      );
      const result = await resp.json().catch(() => ({}));
      if (resp.ok) {
        const all = result.data || [];
        const filtered = all.filter(
          (r) => String(r.dispatch_id) === String(record.id)
        );
        setReports(filtered);
      } else {
        toast.error(result.message || "Failed to load incidence reports");
      }
    } catch (err) {
      toast.error(err.message || "Failed to load incidence reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  const handleChange = (e) => {
    const { id, name, value } = e.target;
    const field = id || name;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageAdd = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(
      (f) => f.type.startsWith("image/") || f.type === "application/pdf"
    );
    if (validFiles.length !== files.length) {
      toast.error("Some files were skipped — only images and PDFs are allowed");
    }
    setImages((prev) => [...prev, ...validFiles]);
    e.target.value = "";
  };

  const handleImageRemove = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!record?.id) {
      toast.error("No dispatch record linked");
      return;
    }
    setIsSubmitting(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        toast.error("Authentication required. Please login again.");
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append("dispatch_id", record.id);
      formDataToSend.append("incident_date", formData.incidentDate);
      formDataToSend.append("incident_type", formData.incidentType);
      formDataToSend.append("severity", formData.severity);
      formDataToSend.append("status", formData.status);
      formDataToSend.append("description", formData.description.trim());
      formDataToSend.append("action_taken", formData.actionTaken.trim());
      formDataToSend.append("reported_by", formData.reportedBy.trim());

      images.forEach((file) => {
        formDataToSend.append("images", file);
      });

      const resp = await fetch(`${API_BASE}/incidence-reports`, {
        method: "POST",
        headers: {
          Authorization: authHeader,
        },
        body: formDataToSend,
      });

      const result = await resp.json().catch(() => ({}));

      if (resp.ok) {
        toast.success(result.message || "Incidence report created successfully");
        setFormData(initialFormState);
        setImages([]);
        setShowForm(false);
        fetchReports();
      } else {
        toast.error(result.message || result.error || "Failed to create incidence report");
      }
    } catch (err) {
      toast.error(err.message || "Failed to create incidence report");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async (id) => {
    setIsDeleting(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        toast.error("Authentication required");
        return;
      }
      const resp = await fetch(
        `${API_BASE}/incidence-reports/${id}`,
        { method: "DELETE", headers: { Authorization: authHeader } }
      );
      const result = await resp.json().catch(() => ({}));
      if (resp.ok) {
        toast.success(result.message || "Incidence report deleted");
        setDeleteReport(null);
        fetchReports();
      } else {
        toast.error(result.message || "Failed to delete incidence report");
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete");
    } finally {
      setIsDeleting(false);
    }
  };

  const exportToCSV = () => {
    if (reports.length === 0) {
      toast.error("No incidence reports to export");
      return;
    }

    const headers = [
      "Incident Date",
      "Incident Type",
      "Severity",
      "Status",
      "Reported By",
      "Description",
      "Action Taken",
      "Date Reported",
      "Truck Number",
      "Driver",
    ];

    const escapeCSV = (value) => {
      if (value == null) return "";
      const str = String(value);
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = reports.map((r) => [
      formatDate(r.incident_date),
      r.incident_type || "",
      r.severity || "",
      r.status || "",
      r.reported_by || "",
      r.description || "",
      r.action_taken || "",
      formatDate(r.created_at),
      record?.truck_number || "",
      record?.driver_name || "",
    ]);

    const csvContent = [
      headers.map(escapeCSV).join(","),
      ...rows.map((row) => row.map(escapeCSV).join(",")),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const truckLabel = (record?.truck_number || "dispatch").replace(/\s+/g, "_");
    link.download = `incidence_reports_${truckLabel}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${reports.length} incidence report(s)`);
  };

  if (!record) {
    return (
      <div className="mt-3 flex flex-col items-center justify-center py-20">
        <MdReportProblem className="mb-4 h-16 w-16 text-gray-300 dark:text-gray-600" />
        <p className="mb-4 text-lg text-gray-500 dark:text-gray-400">
          No dispatch record selected
        </p>
        <button
          onClick={() => navigate("/admin/rcn-delivery/records")}
          className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          <MdArrowBack className="h-5 w-5" />
          Back to Dispatch Records
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3">
      {isSubmitting && <Loading variant="overlay" text="Submitting…" size="lg" />}

      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/rcn-delivery/records")}
            className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
            title="Back"
          >
            <MdArrowBack className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
              Incidence Reports
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Dispatch: <span className="font-medium">{record.truck_number}</span> — {record.driver_name} — {formatDate(record.dispatch_date)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportToCSV}
            disabled={loading || reports.length === 0}
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50"
          >
            <MdFileDownload className="h-5 w-5" />
            Export CSV
          </button>
          <button
            onClick={fetchReports}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50"
          >
            <MdRefresh className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-600"
          >
            <MdAdd className="h-5 w-5" />
            New Incidence
          </button>
        </div>
      </div>

      {/* Dispatch Summary Card */}
      <Card extra="mb-6 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-500/10 text-brand-500">
            <MdLocalShipping className="h-5 w-5" />
          </div>
          <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Truck</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">{record.truck_number}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Driver</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">{record.driver_name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">RCN Type</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">{record.rcn_type}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Warehouse</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">{record.warehouse_location || "—"}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* New Incidence Form */}
      {showForm && (
        <Card extra="mb-6 p-6">
          <form onSubmit={handleSubmit}>
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500 text-white">
                <MdReportProblem className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                  Report New Incidence
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  * Indicates required field
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <InputField
                label="Incident Date *"
                id="incidentDate"
                type="date"
                value={formData.incidentDate}
                onChange={handleChange}
                required
              />

              <div className="flex flex-col">
                <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                  Incident Type *
                </label>
                <select
                  id="incidentType"
                  value={formData.incidentType}
                  onChange={handleChange}
                  required
                  className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm outline-none focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
                >
                  <option value="">Select incident type</option>
                  {incidentTypeOptions.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                  Severity *
                </label>
                <select
                  id="severity"
                  value={formData.severity}
                  onChange={handleChange}
                  required
                  className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm outline-none focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
                >
                  <option value="">Select severity</option>
                  {severityOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                  Status
                </label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm outline-none focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
                >
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <InputField
                label="Reported By *"
                id="reportedBy"
                placeholder="Enter reporter's name"
                value={formData.reportedBy}
                onChange={handleChange}
                required
              />

              <div className="flex flex-col md:col-span-2">
                <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                  Description *
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Describe the incidence in detail..."
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm outline-none focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
                />
              </div>

              <div className="flex flex-col md:col-span-2">
                <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                  Action Taken
                </label>
                <textarea
                  id="actionTaken"
                  value={formData.actionTaken}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Describe any corrective action taken..."
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm outline-none focus:border-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400"
                />
              </div>

              {/* Image Upload */}
              <div className="flex flex-col md:col-span-2">
                <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                  Upload Images
                </label>
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-6 transition-colors hover:border-brand-500 hover:bg-brand-50/50 dark:border-navy-600 dark:bg-navy-800/50 dark:hover:border-brand-500 dark:hover:bg-navy-800">
                  <MdImage className="h-8 w-8 text-gray-400 dark:text-navy-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Click to add images (multiple allowed)
                  </span>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    onChange={handleImageAdd}
                    className="hidden"
                  />
                </label>

                {images.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-3">
                    {images.map((file, index) => (
                      <div
                        key={index}
                        className="group relative flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-2 dark:border-navy-600 dark:bg-navy-800"
                      >
                        {file.type.startsWith("image/") ? (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="h-16 w-16 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gray-100 dark:bg-navy-700">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">PDF</span>
                          </div>
                        )}
                        <div className="flex flex-col">
                          <span className="max-w-[120px] truncate text-xs font-medium text-navy-700 dark:text-white">
                            {file.name}
                          </span>
                          <span className="text-xs text-gray-400">
                            {(file.size / 1024).toFixed(0)} KB
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleImageRemove(index)}
                          className="ml-1 rounded-full p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/20 dark:hover:text-red-400"
                          title="Remove"
                        >
                          <MdClose className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  Accepted formats: Images (JPG, PNG, etc.) and PDF. You can select multiple files.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setFormData(initialFormState);
                  setImages([]);
                }}
                disabled={isSubmitting}
                className="rounded-lg border border-gray-200 bg-white px-6 py-2.5 text-sm font-medium text-navy-700 hover:bg-gray-50 disabled:opacity-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-lg bg-brand-500 px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
              >
                {isSubmitting ? "Submitting…" : "Submit Report"}
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* Incidence Reports List */}
      <Card extra="overflow-hidden p-6">
        <h3 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
          Incidence History
        </h3>

        {loading ? (
          <Loading text="Loading incidence reports…" />
        ) : reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <MdReportProblem className="mx-auto mb-2 h-14 w-14 text-gray-300 dark:text-gray-600" />
            <p className="text-gray-500 dark:text-gray-400">
              No incidence reports for this dispatch
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 flex items-center gap-2 text-sm font-medium text-brand-500 hover:underline"
            >
              <MdAdd className="h-5 w-5" />
              Create first report
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-navy-600">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 dark:border-navy-600 dark:bg-navy-800/50">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Severity</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Reported By</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Description</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-800/30"
                  >
                    <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-navy-700 dark:text-white">
                      {formatDate(r.incident_date)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm text-navy-700 dark:text-white">
                      {r.incident_type}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${SEVERITY_COLORS[r.severity] || "bg-gray-100 text-gray-800"}`}>
                        {r.severity}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[r.status] || "bg-gray-100 text-gray-800"}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm text-navy-700 dark:text-white">
                      {r.reported_by || "—"}
                    </td>
                    <td className="max-w-[200px] truncate px-4 py-4 text-sm text-navy-700 dark:text-white" title={r.description}>
                      {r.description || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setViewReport(r)}
                          className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-brand-50 hover:text-brand-600 dark:text-gray-400 dark:hover:bg-brand-500/20 dark:hover:text-brand-400"
                          title="View details"
                        >
                          <MdVisibility className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => setEditReport(r)}
                          className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-amber-50 hover:text-amber-600 dark:text-gray-400 dark:hover:bg-amber-500/20 dark:hover:text-amber-400"
                          title="Edit"
                        >
                          <MdEdit className="h-5 w-5" />
                        </button>
                        {userRole === "admin" && (
                          <button
                            onClick={() => setDeleteReport(r)}
                            className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-500/20 dark:hover:text-red-400"
                            title="Delete"
                          >
                            <MdDelete className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {viewReport && <ViewDetailsModal report={viewReport} onClose={() => setViewReport(null)} />}
      {editReport && (
        <EditModal
          report={editReport}
          onClose={() => setEditReport(null)}
          onSave={() => {
            setEditReport(null);
            fetchReports();
          }}
        />
      )}
      {deleteReport && (
        <DeleteConfirmModal
          report={deleteReport}
          onClose={() => setDeleteReport(null)}
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
};

export default IncidenceReport;
